// The sections of the signed-in app (spec 49).
//
// A section is one rail entry that owns several pages: one LwPageHeader, a
// row of section tabs under it, and a <router-view> for the page the tab
// selects. This file is the single description of that shape, read by three
// consumers that must agree on it:
//
//   * router/index.js builds each section's parent route and its children
//     from the tabs (the page components are mapped there, so this file pulls
//     in no page and nav.vue can import it for nothing);
//   * components/sidebar/nav/nav.vue derives the rail entry for each section
//     and hides it when every tab is hidden from the current role;
//   * components/sections/SectionLayout.vue draws the header and the tab row.
//
// A tab's `name` is the ROUTE NAME, and those never change (spec 49 section
// 2): the bell, the digest email and the server's notices address pages by
// name. Paths move; names stay. `title` is the browser tab title, `subtitle`
// the line under the section heading while that tab is open, `roles` the
// same gate the rail entry carried before it became a tab.

export const OFFICE = Object.freeze(['admin', 'dispatcher', 'accountant']);
export const BOOKS = Object.freeze(['admin', 'accountant']);

export const SECTIONS = Object.freeze([
  {
    key: 'customers',
    path: '/customers',
    title: 'Customers',
    fa: 'fa-address-book',
    tabs: [
      // The customers list keeps /customers: `path: ''` makes it the
      // section's own address, so every bookmark and every link in the
      // billing lists still lands on it.
      { name: 'Customers', path: '', label: 'Customers', title: 'Customers' },
      // The shared stop library. Mark, 2026-10-12: a location can be
      // independent of a customer, and whether it should instead be
      // customer-scoped is a data-model question this placement leaves open.
      { name: 'Locations', path: 'locations', label: 'Locations', title: 'Locations' },
    ],
  },
  {
    key: 'team',
    path: '/team',
    title: 'Team',
    fa: 'fa-users',
    tabs: [
      { name: 'Drivers', path: 'drivers', label: 'Drivers', title: 'Drivers' },
      // Mark, 2026-10-12: a driver attribute like a licence or a medical card
      // belongs with drivers. This is the Expiring list filtered to driver
      // and subhauler credentials; the equipment and company half is the
      // Equipment section's Expiring tab, and each links to the other.
      {
        name: 'TeamExpiring',
        path: 'expiring',
        label: 'Expiring',
        title: 'Expiring credentials',
        subtitle: 'Driver and subhauler credentials that have expired or expire soon.',
        roles: OFFICE,
      },
      {
        name: 'Scheduling',
        path: 'schedule',
        label: 'Schedule',
        title: 'Scheduling',
        roles: OFFICE,
      },
      {
        name: 'Settlements',
        path: 'settlements',
        label: 'Settlements',
        title: 'Settlements',
        roles: OFFICE,
      },
      // The office logins, invites and roles (the page that WAS "Team"), here
      // because linking a driver-role login to a roster driver happens on it.
      { name: 'Team', path: 'users', label: 'Users', title: 'Users' },
    ],
  },
  {
    key: 'equipment',
    path: '/equipment',
    title: 'Equipment',
    fa: 'fa-truck',
    tabs: [
      { name: 'Trucks', path: '', label: 'Equipment', title: 'Equipment' },
      {
        name: 'FleetMaintenance',
        path: 'maintenance',
        label: 'Maintenance',
        title: 'Maintenance',
        subtitle:
          'Held units, PM coming due, open work orders and inspection report defects, across the fleet.',
        roles: OFFICE,
      },
      {
        name: 'FleetExpiring',
        path: 'expiring',
        label: 'Expiring',
        title: 'Expiring',
        subtitle:
          'Truck, trailer and company credentials that have expired or expire soon: registrations, inspections, insurance, the IFTA licence, UCR and Form 2290.',
        roles: OFFICE,
      },
    ],
  },
  {
    key: 'reports',
    path: '/reports',
    title: 'Reports',
    fa: 'fa-bar-chart',
    tabs: [
      { name: 'IftaReport', path: 'ifta', label: 'IFTA miles', title: 'IFTA miles' },
      { name: 'FuelPurchases', path: 'fuel', label: 'Fuel', title: 'Fuel', roles: OFFICE },
      { name: 'Expenses', path: 'expenses', label: 'Expenses', title: 'Expenses', roles: BOOKS },
      {
        name: 'Profit',
        path: 'profit',
        label: 'Profit',
        title: 'Profit',
        subtitle:
          'Profit per load, run, truck and customer. Estimates are labelled; actual figures replace them after delivery. This is not a profit-and-loss statement: your books stay in your accounting system.',
        roles: BOOKS,
      },
    ],
  },
  {
    key: 'settings',
    path: '/settings',
    title: 'Settings',
    fa: 'fa-cog',
    tabs: [
      { name: 'Integrations', path: 'integrations', label: 'Integrations', title: 'Integrations' },
      { name: 'Devices', path: 'devices', label: 'Devices', title: 'Devices' },
      { name: 'Preferences', path: 'preferences', label: 'Preferences', title: 'Preferences' },
      {
        name: 'DailyDigest',
        path: 'digest',
        label: 'Daily digest',
        title: 'Daily digest',
        roles: OFFICE,
      },
      {
        name: 'LadewayBilling',
        path: 'billing',
        label: 'Ladeway billing',
        title: 'Ladeway billing',
        roles: BOOKS,
      },
      {
        name: 'FactoringSettings',
        path: 'factoring',
        label: 'Factoring',
        title: 'Factoring',
        roles: BOOKS,
      },
    ],
  },
]);

export function findSection(key) {
  return SECTIONS.find(section => section.key === key) || null;
}

export function sectionOfRoute(routeName) {
  return SECTIONS.find(section => section.tabs.some(tab => tab.name === routeName)) || null;
}
